import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { Topic } from "@/types/content";
import { formatHeat } from "@/utils/format";

export default function Hero({ featured }: { featured: Topic }) {
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();

  const handleSearch = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/rank?q=${encodeURIComponent(q)}` : "/rank");
  };

  return (
    <section className="relative w-full overflow-hidden border-b border-background-200 bg-background-100">
      <div className="absolute inset-0" aria-hidden="true">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20editorial%20artwork%20of%20scattered%20paper%20fragments%20and%20glowing%20signal%20dots%20spreading%20across%20a%20warm%20cream%20and%20deep%20red%20background%2C%20minimal%20geometric%20composition%2C%20soft%20dramatic%20lighting%2C%20wide%20banner%2C%20high%20detail%2C%20no%20text&width=1600&height=900&seq=hero-radar-bg-01&orientation=landscape"
          alt=""
          className="h-full w-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background-100 via-background-100/90 to-background-100/40"></div>
      </div>

      <div className="relative mx-auto grid w-full max-w-[1240px] grid-cols-1 gap-10 px-4 py-12 md:px-6 md:py-16 lg:grid-cols-12 lg:gap-12">
        <div className="flex flex-col justify-center lg:col-span-7">
          <span className="inline-flex w-fit items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-3 py-1.5 text-xs text-primary-700">
            <span className="flex h-2 w-2 items-center justify-center">
              <span className="h-2 w-2 animate-pulse rounded-full bg-primary-500"></span>
            </span>
            实时更新中 · 今日已收录 36 起热议事件
          </span>

          <h1 className="mt-5 font-heading text-3xl font-bold leading-tight tracking-tight text-foreground-950 md:text-5xl">
            <span className="mb-3 block font-label text-xs font-medium tracking-[0.28em] text-foreground-500">
              瓜点 · 网络爆料雷达
            </span>
            今日网络爆料
            <br />
            <span className="text-primary-600">看这一页就够了</span>
          </h1>

          <p className="mt-5 max-w-xl text-sm leading-relaxed text-foreground-600 md:text-base">
            聚合网络爆料今日热门事件、最新头条与全网热议榜单，快速掌握当前最受关注的爆料话题。吃瓜追更，一站看全。
          </p>

          <form
            onSubmit={handleSearch}
            className="mt-7 flex w-full max-w-lg flex-col gap-2 sm:flex-row sm:items-center sm:gap-0 sm:rounded-md sm:border sm:border-background-300 sm:bg-background-50 sm:p-1.5"
            role="search"
          >
            <label htmlFor="hero-search" className="sr-only">
              搜索爆料话题
            </label>
            <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2.5 sm:border-0 sm:bg-transparent sm:py-1.5">
              <span className="flex h-5 w-5 items-center justify-center text-foreground-400">
                <i className="ri-search-line"></i>
              </span>
              <input
                id="hero-search"
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索爆料话题、关键词或分类"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
            </div>
            <button
              type="submit"
              className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 sm:py-2.5"
            >
              搜索话题
            </button>
          </form>

          <dl className="mt-9 grid grid-cols-3 gap-4 border-t border-background-300 pt-6">
            {[
              { label: "今日收录话题", value: "128" },
              { label: "上榜热议事件", value: "36" },
              { label: "覆盖讨论平台", value: "9" },
            ].map((s) => (
              <div key={s.label}>
                <dt className="text-xs text-foreground-500">{s.label}</dt>
                <dd className="mt-1 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                  {s.value}
                </dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="lg:col-span-5">
          <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
            <div className="flex items-center justify-between border-b border-background-200 bg-primary-500 px-4 py-2.5 text-background-50">
              <span className="flex items-center gap-2 text-sm font-medium">
                <i className="ri-trophy-line"></i>
                今日榜首
              </span>
              <span className="text-xs text-primary-100">每小时刷新</span>
            </div>
            <Link to={`/topic/${featured.id}`} className="group block">
              <div className="h-[210px] w-full overflow-hidden md:h-[240px]">
                <img
                  src={featured.cover}
                  alt={`${featured.title} 相关配图`}
                  title={`${featured.title} - 网络爆料今日榜首`}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                />
              </div>
              <div className="p-4 md:p-5">
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="rounded-full bg-accent-100 px-2.5 py-1 text-accent-900">
                    {featured.category}
                  </span>
                  <span className="text-foreground-500">更新于 {featured.updatedTime}</span>
                </div>
                <h2 className="mt-3 text-lg font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
                  {featured.title}
                </h2>
                <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">
                  {featured.summary}
                </p>
                <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3">
                  <span className="flex items-center gap-1.5 text-sm font-medium text-primary-600">
                    <i className="ri-fire-fill"></i>
                    热度 {formatHeat(featured.heat)}
                  </span>
                  <span className="flex items-center gap-1 text-xs text-foreground-500">
                    查看详情
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}